/**
 * This will be removed in favour of @confluence/relay-network/src/calculateBackOffRange.ts when we move to Relay.
 */

// copied from Trello
import {
	getDefaultStatusMultipliers,
	getMaxDelay,
	getReconnectDelayBounds,
	jitterStatusMultipliers as defaultJitterStatusMultipliers,
} from './backOffConfig.ts';

/**
 * Calculates the range of exponential back-off for reconnection attempts.
 * The range is calculated based on the status of the user, the number of attempts, and the health of the connection.
 *
 * @param {MonitorStatus} status - The status of the user.
 * @param {number} attemptsCount - The number of reconnection attempts.
 * @param {Record<MonitorStatus, number>} [statusMultipliersOverride] - Custom multipliers for the status.
 * @param {Record<MonitorStatus, number>} [jitterStatusMultipliersOverride] - Custom multipliers for the jitter by status.
 * @param {ConnectionHealth} [connectionHealth='base'] - The health of the connection.
 * @param {number} [baseLowerBoundOverride] - Custom lower bound for the back-off range.
 * @param {number} [baseUpperBoundOverride] - Custom upper bound for the back-off range.
 * @param {number} [maxDelayOverride] - Custom maximum delay for reconnection attempts.
 * @returns {number[]} An array of `[lower, upper]` bounds.
 *
 * @example
 * const range = calculateBackOffRange({
 *  status: 'active',
 *  attemptsCount: 0,
 * });
 * // range = [800, 1800]
 */
export function calculateBackOffRange({
	status,
	attemptsCount,
	statusMultipliersOverride,
	jitterStatusMultipliersOverride,
	connectionHealth = 'base',
	baseLowerBoundOverride,
	baseUpperBoundOverride,
	maxDelayOverride,
}: {
	status: 'idle' | 'active';
	attemptsCount: number;
	statusMultipliersOverride?: Record<'idle' | 'active', number>;
	jitterStatusMultipliersOverride?: Record<'idle' | 'active', number>;
	connectionHealth?: 'base' | 'serverIncident';
	baseLowerBoundOverride?: number;
	baseUpperBoundOverride?: number;
	maxDelayOverride?: number;
}): [number, number] {
	const [baseLowerBound, baseUpperBound] = getReconnectDelayBounds(connectionHealth);
	const maxDelay = maxDelayOverride ?? getMaxDelay({ status, connectionHealth });
	const statusMultipliers = statusMultipliersOverride ?? getDefaultStatusMultipliers();
	const jitterStatusMultipliers = jitterStatusMultipliersOverride ?? defaultJitterStatusMultipliers;
	const attemptsMultiplier = Math.pow(2, attemptsCount);
	const totalMultiplier = statusMultipliers[status] * attemptsMultiplier;
	const jitterMultiplier = jitterStatusMultipliers[status];

	const maxFirstActiveDelay = 2000;

	const maxFirstIdleDelay = 60000;
	let tempUpperOverride;
	if (attemptsCount > 0) {
		tempUpperOverride = undefined;
	} else if (status === 'active') {
		tempUpperOverride = maxFirstActiveDelay;
	} else {
		tempUpperOverride = maxFirstIdleDelay;
	}

	const lowerBound = (baseLowerBoundOverride ?? baseLowerBound) * totalMultiplier;
	const upperBound =
		tempUpperOverride ??
		(baseUpperBoundOverride ?? baseUpperBound) * totalMultiplier * jitterMultiplier;

	return [Math.min(lowerBound, maxDelay), Math.min(upperBound, maxDelay)];
}
